<template>
    <CompassPanel
        ref="cardsPanel"
        class="cards-settings-panel"
        :modelValue="isVisible"
        size="small"
        :title="dashboardManagementLabel"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="$emit('close')"
    >
        <div class="flex-row ai-center jc-space">
            <div class="typographies-paragraph-large-bold">
                {{ $l10n.__('Afficher / Masquer les cartes') }}
            </div>
            <CardCounter :current="cards.length" :max="max" class="m-0" />
        </div>

        <!-- HAS REPORTS RIGHTS -->
        <CompassTabs v-if="hasReportsAccess" v-model="selectedTab">
            <CompassTabPane :label="$t.__('dashboard.CardsSettingsPanel.card')" name="cards">
                <CompassCheckboxGroup
                    v-if="!!cardsCrm.length"
                    :label="$t.__('dashboard.CardsSettingsPanel.crm')"
                >
                    <CompassCheckbox
                        v-for="card in cardsCrm"
                        ref="cardsCheckboxCrm"
                        :key="card.type"
                        :modelValue="card.label"
                        :checked="isActiveCard(card.type)"
                        :disabled="disabledCard(card.type)"
                        data-test="cards-checkbox-crm"
                        @change="checkCard(card)"
                    />
                </CompassCheckboxGroup>
                <CompassCheckboxGroup
                    v-if="!!cardsInvoicing.length"
                    :label="$t.__('dashboard.CardsSettingsPanel.invoicing')"
                    class="mt-3"
                >
                    <CompassCheckbox
                        v-for="card in cardsInvoicing"
                        ref="cardsCheckboxInvoicing"
                        :key="card.type"
                        :modelValue="card.label"
                        :checked="isActiveCard(card.type)"
                        :disabled="disabledCard(card.type)"
                        data-test="cards-checkbox-invoicing"
                        @change="checkCard(card)"
                    />
                </CompassCheckboxGroup>
                <CompassCheckboxGroup
                    v-if="!!cardsAccounting.length"
                    :label="$t.__('common.labels.accounting')"
                    class="mt-3"
                >
                    <CompassCheckbox
                        v-for="card in cardsAccounting"
                        ref="cardsCheckboxAccounting"
                        :key="card.type"
                        :modelValue="card.label"
                        :checked="isActiveCard(card.type)"
                        :disabled="disabledCard(card.type)"
                        data-test="cards-checkbox-accounting"
                        @change="checkCard(card)"
                    />
                </CompassCheckboxGroup>
            </CompassTabPane>
            <CompassTabPane :label="$t.__('common.labels.reports')" name="reports">
                <CompassAlert
                    v-if="reportCardLimit !== true"
                    :description="
                        $t.__('dashboard.CardsSettingsPanel.can_add_up_to_x_reports_tiles', {
                            reportCardLimit: reportCardLimit,
                        })
                    "
                />
                <CompassInput
                    v-model="searchInput"
                    class="flex-row ai-center wp-10"
                    :placeholder="searchReportPlaceholderLabel"
                    suffixIcon="search"
                />
                <CompassCheckboxGroup
                    v-if="!!cardsReportProspecting.length"
                    class="mt-3"
                    :label="getLicenceLabel('pack_prospecting_sale')"
                >
                    <div v-for="card in cardsReportProspecting" :key="card.type" class="container">
                        <CompassCheckbox
                            ref="cardsCheckboxReportProspecting"
                            :key="card.type"
                            :modelValue="card.label"
                            :checked="isActiveCard(card.type)"
                            :disabled="disabledCard(card.type) || card.has_to_upgrade"
                            @change="checkCard(card)"
                        />

                        <CompassTooltip
                            v-if="card.has_to_upgrade"
                            placement="bottom-end"
                            :content="getUpsellTooltip"
                            contentClass="badge-tooltip"
                        >
                            <CompassFeatureBadge class="badge" variant="upsell" size="small" />
                        </CompassTooltip>
                    </div>
                </CompassCheckboxGroup>
                <CompassCheckboxGroup
                    v-if="!!cardsReportInvoicing.length"
                    class="mt-3"
                    :label="getLicenceLabel('pack_invoicing_management')"
                >
                    <div v-for="card in cardsReportInvoicing" :key="card.type" class="container">
                        <CompassCheckbox
                            :key="card.type"
                            ref="cardsCheckboxReportInvoicing"
                            class="container"
                            :modelValue="card.label"
                            :checked="isActiveCard(card.type)"
                            :disabled="disabledCard(card.type) || card.has_to_upgrade"
                            @change="checkCard(card)"
                        />
                        <CompassTooltip
                            v-if="card.has_to_upgrade"
                            placement="bottom-end"
                            :content="getUpsellTooltip"
                            contentClass="badge-tooltip"
                        >
                            <CompassFeatureBadge class="badge" variant="upsell" size="small" />
                        </CompassTooltip>
                    </div>
                </CompassCheckboxGroup>
            </CompassTabPane>
        </CompassTabs>

        <!-- HAS NOT REPORTS RIGHTS -->
        <div v-else class="mt-4 pt-1">
            <CompassCheckboxGroup
                v-if="!!cardsCrm"
                :label="$t.__('dashboard.CardsSettingsPanel.crm')"
            >
                <CompassCheckbox
                    v-for="card in cardsCrm"
                    ref="cardsCheckboxCrm"
                    :key="card.type"
                    :modelValue="card.label"
                    :checked="isActiveCard(card.type)"
                    :disabled="disabledCard(card.type)"
                    data-test="cards-checkbox-crm"
                    @change="checkCard(card)"
                />
            </CompassCheckboxGroup>
            <CompassCheckboxGroup
                v-if="!!cardsInvoicing"
                :label="$t.__('dashboard.CardsSettingsPanel.invoicing')"
                class="mt-3"
            >
                <CompassCheckbox
                    v-for="card in cardsInvoicing"
                    ref="cardsCheckboxInvoicing"
                    :key="card.type"
                    :modelValue="card.label"
                    :checked="isActiveCard(card.type)"
                    :disabled="disabledCard(card.type)"
                    data-test="cards-checkbox-invoicing"
                    @change="checkCard(card)"
                />
            </CompassCheckboxGroup>
            <CompassCheckboxGroup
                v-if="!!cardsAccounting"
                :label="$t.__('common.labels.accounting')"
                class="mt-3"
            >
                <CompassCheckbox
                    v-for="card in cardsAccounting"
                    ref="cardsCheckboxAccounting"
                    :key="card.type"
                    :modelValue="card.label"
                    :checked="isActiveCard(card.type)"
                    :disabled="disabledCard(card.type)"
                    data-test="cards-checkbox-accounting"
                    @change="checkCard(card)"
                />
            </CompassCheckboxGroup>
        </div>
    </CompassPanel>
</template>

<script>
import CardCounter from 'vcomponents/dashboard/CardCounter.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import QuotaApi from 'api/sellsyaccountmanager/subscription';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters, mapState } = createNamespacedHelpers('corp');
const { mapState: mapStateSubscription } = createNamespacedHelpers('subscriptionManager');

export default {
    components: {
        CardCounter,
    },
    props: {
        items: {
            type: [Array, Object],
            required: true,
        },
        max: {
            type: Number,
            required: true,
        },
        min: {
            type: Number,
            required: true,
        },
        defaultReportCardLimit: {
            type: Number,
            required: true,
        },
        defaultOpenedTab: {
            type: String,
            required: true,
        },
        isVisible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'save', 'add', 'removed'],
    data() {
        return {
            selectedTab: '',
            searchInput: '',
            cards: [],
            cardsReportSelected: [],
            reportCardLimit: 0,
            primaryAction: {
                label: this.$l10n.__('Appliquer'),
                action: () => this.$emit('save'),
            },
            cancelButtonProps: {
                label: this.$l10n.__('Annuler'),
            },
            searchReportPlaceholderLabel: this.$l10n.__('Rechercher un rapport'),
            dashboardManagementLabel: window.i18n.__('Gérer le tableau de bord'),
        };
    },
    computed: {
        ...mapState(['pricingVersion']),
        ...mapStateSubscription(['versionedPlans', 'versionedLicenses']),
        cardsCrm() {
            return this.items.filter(
                (item) => item.category === 'crm' && this.getTypeCard(item.type) !== 'cumulio',
            );
        },
        cardsInvoicing() {
            return this.items.filter(
                (item) =>
                    item.category === 'invoicing' && this.getTypeCard(item.type) !== 'cumulio',
            );
        },
        cardsAccounting() {
            return this.items.filter(
                (item) =>
                    item.category === 'accounting' && this.getTypeCard(item.type) !== 'cumulio',
            );
        },
        cardsReportInvoicing() {
            return _.sortBy(
                this.searchReport.filter(
                    (card) =>
                        card.category === 'invoicing' && this.getTypeCard(card.type) === 'cumulio',
                ),
                'label',
            );
        },
        cardsReportProspecting() {
            return _.sortBy(
                this.searchReport.filter(
                    (card) =>
                        card.category === 'prospecting' &&
                        this.getTypeCard(card.type) === 'cumulio',
                ),
                'label',
            );
        },
        cardsReport() {
            return this.items.filter((card) => this.getTypeCard(card.type) === 'cumulio');
        },
        hasMaxCard() {
            return this.cards.length >= this.max;
        },
        hasMinCard() {
            return this.cards.length <= this.min;
        },
        hasMaxReportCards() {
            // unlimited card
            if (this.reportCardLimit === true) {
                return false;
            }
            // limit reached ?
            return (
                this.reportCardLimit !== null &&
                this.cardsReportSelected.length >= this.reportCardLimit
            );
        },
        searchReport() {
            return this.searchInput
                ? this.items.filter((item) => {
                    return item.label
                        .toLowerCase()
                        .includes(this.searchInput.toLowerCase().trim());
                })
                : this.items;
        },
        hasReportsAccess() {
            return this.cardsReport?.length > 0;
        },
        ...mapGetters(['corpId']),
        getUpsellTooltip() {
            const firstPlanLabel = this.getPlanLabel(this.versionedPlans[1].code);
            const secondPlanLabel = this.getPlanLabel(this.versionedPlans[2].code);

            return this.$l10n.__(
                'Passez au plan %(firstPlanLabel)s ou %(secondPlanLabel)s pour profiter de tous les rapports.',
                {
                    firstPlanLabel: firstPlanLabel,
                    secondPlanLabel: secondPlanLabel,
                },
            );
        },
    },
    created() {
        this.items
            .filter((item) => item.active)
            .forEach((item) => {
                this.cards.push(item.type);
            });

        this.items
            .filter((item) => item.active && this.getTypeCard(item.type) === 'cumulio')
            .forEach((item) => {
                this.cardsReportSelected.push(item.type);
            });

        this.selectedTab = this.defaultOpenedTab;

        this.initReportCardLimit();
        EventBus.on('cardSettingPanel:remove', this.removeCard);
    },
    methods: {
        initReportCardLimit() {
            if (!this.hasReportsAccess) {
                return;
            }

            const localStorage = new LocalStorage(`dashboard_${this.corpId}`, `report_card_limit`);

            this.reportCardLimit = localStorage.get('payload') ?? this.defaultReportCardLimit;

            if (this.pricingVersion < 5) {
                return;
            }

            if (localStorage.get('payload') === null) {
                QuotaApi.getQuotasAndCredits()
                    .then(({ data: quotasAndCredit }) => {
                        for (const [key, value] of Object.entries(quotasAndCredit)) {
                            if (key !== 'report_cards') {
                                continue;
                            }
                            // if value.limit is null, it means unlimited (set true in this context)
                            const limit = value.limit ?? true;
                            localStorage.setex(limit, 3600000); // 3 600 000 ms => 1 hour for ttl
                        }
                    })
                    .then(() => (this.reportCardLimit = localStorage.get('payload')));
            }
        },
        checkCard(card) {
            this.cards.includes(card.type) ? this.remove(card) : this.add(card);
        },
        add(card) {
            if (this.hasMaxCard) {
                Push.error(
                    this.$l10n.__(
                        'Vous ne pouvez pas sélectionner plus de %s cartes sur votre tableau de bord.',
                        this.max,
                    ),
                    this.$l10n.__('Limite haute atteinte'),
                );
                return;
            } else if (this.hasMaxReportCards && card.type === 'cumulio') {
                Push.error(
                    this.$l10n.__(
                        'Vous ne pouvez pas sélectionner plus de %s cartes de type cumul io sur votre tableau de bord.',
                        this.reportCardLimit,
                    ),
                    this.$l10n.__('Limite haute atteinte'),
                );
                return;
            }

            this.$emit('add', card.type);
            if (this.getTypeCard(card.type) === 'cumulio') {
                this.cardsReportSelected.push(card.type);
            }
            this.cards.push(card.type);
        },
        remove(card) {
            if (this.hasMinCard) {
                Push.error(
                    this.$l10n.__(
                        'Vous devez sélectionner au moins une carte sur votre tableau de bord.',
                    ),
                    this.$l10n.__('Limite basse atteinte'),
                );
                return;
            }

            this.removeCard(card);
            this.$emit('removed', card.type, 'settings', false);
        },
        deleteFromArray(card, arrayCard) {
            arrayCard.splice(
                this.cards.findIndex((item) => item === card.type),
                1,
            );
        },
        removeCard(card) {
            const findItem = card;
            if (this.getTypeCard(findItem.type) === 'cumulio') {
                this.deleteFromArray(findItem, this.cards);
                this.deleteFromArray(findItem, this.cardsReportSelected);
            } else {
                this.deleteFromArray(findItem, this.cards);
            }
        },
        isActiveCard(type) {
            return this.cards.includes(type);
        },
        disabledCard(type) {
            if (
                this.hasMaxCard ||
                (this.hasMaxReportCards && this.getTypeCard(type) === 'cumulio')
            ) {
                return !this.isActiveCard(type);
            } else if (this.hasMinCard) {
                return this.isActiveCard(type);
            }
        },
        getTypeCard(cardType) {
            return cardType.split('/')[0];
        },
        getPlanLabel(planCode) {
            const currentPlan = this.versionedPlans.find(
                (versionedPlan) => versionedPlan.code === planCode,
            );

            return currentPlan.label;
        },
        getLicenceLabel(licenceCode) {
            const currentLicence = this.versionedLicenses.find(
                (versionedLicense) => versionedLicense.code === licenceCode,
            );

            return currentLicence.label;
        },
    },
};
</script>

<style lang="scss">
.cards-settings-panel {
    .compass-tabs {
        margin-top: 28px;
    }
    .container {
        position: relative;
        display: flex;
        gap: var(--spacing-0);
    }
}
.badge-tooltip.compass-tooltip__item {
    z-index: 1001;
    max-width: 176px;
}
</style>
